import {
    Component,
    OnInit,
    OnDestroy,
    EventEmitter,
    Output,
    ViewChild,
    ElementRef,
  } from '@angular/core';
  import { Router, NavigationEnd } from '@angular/router';
  import { TranslateService } from '@ngx-translate/core';
  import { CommonCommunicationService } from 'src/app/core/services/commonCommunication.service';
  import { Subscription } from 'rxjs';
  
  import { MENU } from './menu';
  import { MenuItem } from './menu.model';
  
  @Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
  })
  export class SidebarComponent implements OnInit, OnDestroy {
    menu: any;
    menuItems: MenuItem[] = [];
  
    @ViewChild('sideMenu') sideMenu!: ElementRef;
    @Output() mobileMenuButtonClicked = new EventEmitter();
  
    private routerSubscription: Subscription;
  
    constructor(
      private router: Router,
      private translate: TranslateService,
      private ccs: CommonCommunicationService
    ) {
      translate.setDefaultLang('en');
  
      // Subscribe to router events for robust menu activation
      this.routerSubscription = this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          setTimeout(() => this._initActiveMenu(), 0);
        }
      });
    }
  
    ngOnInit(): void {
      // Menu Items
      let menuData: any = MENU;
      this.ccs.getModuleList().subscribe((res: any) => {
        if (res) {
          let menu: any = [];
          let moduleList = res
            .filter((item: any) => item.is_view)
            .map((item: any) => item.moduleName);
          
          menuData.forEach((item: any) => {
            let data: any = item;
            if (moduleList.includes(data.label)) {
              let subdata: any = [];
              if (data.subItems) {
                data.subItems.forEach((subitem: any) => {
                  if (moduleList.includes(subitem.label) || subitem.label === 'Print Barcode') {
                    subdata.push(subitem);
                  }
                });
                data.subItems = subdata;
              }
              menu.push(data);
            }
          });
          this.menuItems = menu;
  
          // Initialize active menu after items are loaded
          setTimeout(() => this._initActiveMenu(), 0);
        }
      });
    }
  
    ngOnDestroy(): void {
      // Unsubscribe to prevent memory leaks
      if (this.routerSubscription) {
        this.routerSubscription.unsubscribe();
      }
    }
  
    /**
     * Initializes the active menu item and its parents.
     * This is the core logic that runs on every navigation.
     */
    private _initActiveMenu(): void {
      const navBar = document.getElementById('navbar-nav');
      if (!navBar) return;
  
      // Wait a bit for routerLinkActive to be applied
      setTimeout(() => {
        // Find the current active link (set by routerLinkActive)
        const currentActiveLink = navBar.querySelector('a[routerLink].active');
        if (currentActiveLink) {
          this.activateParentDropdown(currentActiveLink);
        }
      }, 50);
    }
  
    /**
     * Activates a menu item and all its parent dropdowns.
     * @param item The active menu link element.
     */
    private activateParentDropdown(item: Element): void {
      // Don't remove active class from the item - let routerLinkActive handle it
  
      // Get the parent collapse element
      let parentCollapse = item.closest('.collapse.menu-dropdown');
  
      while (parentCollapse) {
        // Mark the collapse as shown
        parentCollapse.classList.add('show');
        
        // Get the trigger link for this collapse
        const triggerLink = parentCollapse.parentElement?.querySelector('a[data-bs-toggle="collapse"]');
        if (triggerLink) {
          triggerLink.setAttribute('aria-expanded', 'true');
          
          // Move to the next parent level
          parentCollapse = triggerLink.closest('.collapse.menu-dropdown');
        } else {
          break;
        }
      }
    }
  
    /**
     * Handles the manual toggling of top-level menu items.
     * Only toggles when clicking on parent items, not when navigating to child routes.
     */
    toggleItem(event: any): void {
      event.preventDefault();
      const anchor = event.currentTarget as HTMLAnchorElement;
      const subMenu = anchor.nextElementSibling as HTMLElement;
  
      if (!subMenu) return;
  
      const isCurrentlyOpen = subMenu.classList.contains('show');
  
      if (isCurrentlyOpen) {
        // Close current menu
        subMenu.classList.remove('show');
        anchor.setAttribute('aria-expanded', 'false');
      } else {
        // Close all other top-level menus first
        const allTopMenus = document.querySelectorAll('#navbar-nav > .nav-item > .collapse.menu-dropdown');
        allTopMenus.forEach(menu => {
          if (menu !== subMenu) {
            menu.classList.remove('show');
            const menuTrigger = menu.parentElement?.querySelector('.menu-link');
            if (menuTrigger) {
              menuTrigger.setAttribute('aria-expanded', 'false');
            }
          }
        });
  
        // Open the clicked menu
        subMenu.classList.add('show');
        anchor.setAttribute('aria-expanded', 'true');
      }
    }
  
    /**
     * Handles toggling of sub-menu items.
     * Only affects the clicked submenu, doesn't close parent menus.
     */
    toggleSubItem(event: any): void {
      event.preventDefault();
      const anchor = event.currentTarget as HTMLAnchorElement;
      const subMenu = anchor.nextElementSibling as HTMLElement;
  
      if (!subMenu) return;
  
      const isCurrentlyOpen = subMenu.classList.contains('show');
  
      // Close other sub-menus at the same level (siblings)
      const parentMenu = anchor.closest('.menu-dropdown');
      if (parentMenu) {
        const siblingSubMenus = parentMenu.querySelectorAll('.sub-menu');
        siblingSubMenus.forEach((menu: any) => {
          if (menu !== subMenu) {
            menu.classList.remove('show');
            const trigger = menu.previousElementSibling;
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          }
        });
      }
  
      // Toggle current sub-menu
      if (isCurrentlyOpen) {
        subMenu.classList.remove('show');
        anchor.setAttribute('aria-expanded', 'false');
      } else {
        subMenu.classList.add('show');
        anchor.setAttribute('aria-expanded', 'true');
      }
    }
  
    /**
     * Handles toggling of extra sub-menu items (third level).
     */
    toggleExtraSubItem(event: any): void {
      event.preventDefault();
      const anchor = event.currentTarget as HTMLAnchorElement;
      const subMenu = anchor.nextElementSibling as HTMLElement;
  
      if (subMenu) {
        // Close other extra sub-menus at the same level
        const siblingExtraSubMenus = document.querySelectorAll('.extra-sub-menu');
        siblingExtraSubMenus.forEach((menu: any) => {
          if (menu !== subMenu) {
            menu.classList.remove('show');
            const trigger = menu.previousElementSibling;
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          }
        });
  
        // Toggle current extra sub-menu
        const isOpen = subMenu.classList.contains('show');
        if (isOpen) {
          subMenu.classList.remove('show');
          anchor.setAttribute('aria-expanded', 'false');
        } else {
          subMenu.classList.add('show');
          anchor.setAttribute('aria-expanded', 'true');
        }
      }
      
      this.closeSidebar();
    }
  
    /**
     * Close sidebar on mobile devices after menu interaction.
     */
    closeSidebar(): void {
      const w = window.innerWidth;
      if (w <= 768) { // Changed from 600 to 768 for better mobile detection
        this.SidebarHide();
      }
    }
  
    /**
     * Handle menu item click for final navigation items
     */
    onMenuItemClick(): void {
      this.closeSidebar();
    }
  
    /**
     * Returns true or false if given menu item has child or not
     * @param item menuItem
     */
    hasItems(item: MenuItem): boolean {
      return item.subItems !== undefined ? item.subItems.length > 0 : false;
    }
  
    /**
     * Toggle the menu bar when having mobile screen
     */
    toggleMobileMenu(event: any): void {
      const sidebarsize = document.documentElement.getAttribute('data-sidebar-size');
      if (sidebarsize === 'sm-hover-active') {
        document.documentElement.setAttribute('data-sidebar-size', 'sm-hover');
      } else {
        document.documentElement.setAttribute('data-sidebar-size', 'sm-hover-active');
      }
    }
  
    /**
     * Hide sidebar modal
     */
    SidebarHide(): void {
      document.body.classList.remove('vertical-sidebar-enable');
    }
  }